/**
 * お気に入り（テンプレート）セクション - 共通コンポーネント
 *
 * 食事追加と食事/テンプレート編集の両方で同一UIを使用する。
 * チェックボックスで複数テンプレートを選択 + 倍率調整。
 */

import { ArrowUpDown, Loader2, Pencil, Plus, Trash2 } from 'lucide-react'
import { useEffect, useState } from 'react'
import { toast } from 'sonner'

import { deleteTemplate, getBodyProfile, listTemplates, updateBodyProfile } from '@/api/services/nutrition'
import type { FoodTemplate, TemplateSortOrder } from '@/api/types/nutrition'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { confirmDialog } from '@/lib/confirm-dialog'

import { MultiplierInput } from './multiplier-input'
import { TemplatePickerList } from './template-picker-list'

const SORT_OPTIONS: { value: TemplateSortOrder; label: string }[] = [
  { value: 'created_desc', label: '追加が新しい順' },
  { value: 'last_used_desc', label: '最近使った順' },
  { value: 'use_count_desc', label: 'よく使う順' },
]

const DEFAULT_SORT: TemplateSortOrder = 'created_desc'

type TemplateFavoritesSectionProps = {
  /** 外部から渡すテンプレート一覧（省略時は内部でfetch） */
  templates?: FoodTemplate[]
  /** 選択中のテンプレートID */
  selectedIds: Set<number>
  onToggle: (id: number) => void
  /** 倍率マップ */
  multipliers: Map<number, string>
  onMultiplierChange: (id: number, value: string) => void
  /** 自分自身を除外するためのID（テンプレート編集時） */
  excludeTemplateId?: number
  /** テンプレート編集ボタン押下時 */
  onEditTemplate?: (template: FoodTemplate) => void
  /** テンプレート新規登録ボタン押下時 */
  onCreateTemplate?: () => void
  /** テンプレート変更（削除含む）後に親へ通知 */
  onTemplateChanged?: () => void
  /** 一覧の最大高さ（Tailwind のクラス。省略時は TemplatePickerList の既定） */
  listMaxHeight?: string
}

export function TemplateFavoritesSection({
  templates: externalTemplates,
  selectedIds,
  onToggle,
  multipliers,
  onMultiplierChange,
  excludeTemplateId,
  onEditTemplate,
  onCreateTemplate,
  onTemplateChanged,
  listMaxHeight,
}: TemplateFavoritesSectionProps) {
  // テンプレートが外部から渡されない場合は内部で取得
  const [internalTemplates, setInternalTemplates] = useState<FoodTemplate[]>([])
  const [loading, setLoading] = useState(!externalTemplates)
  const [sortOrder, setSortOrder] = useState<TemplateSortOrder>(DEFAULT_SORT)
  const [sortInitialized, setSortInitialized] = useState(false)

  // 初回: BodyProfile からソート順を読み込む
  useEffect(() => {
    let cancelled = false
    getBodyProfile()
      .then((profile) => {
        if (cancelled) return
        setSortOrder(profile.template_sort_order ?? DEFAULT_SORT)
      })
      .catch((err) => {
        // 取得失敗時はデフォルト維持。復旧時のデバッグ用に警告のみ出す
        console.warn('[TemplateFavoritesSection] getBodyProfile failed, using default sort:', err)
      })
      .finally(() => {
        if (!cancelled) setSortInitialized(true)
      })
    return () => {
      cancelled = true
    }
  }, [])

  // 内部fetchモード: sortOrder が確定してから取得
  useEffect(() => {
    if (externalTemplates) return
    if (!sortInitialized) return
    let cancelled = false
    setLoading(true)
    listTemplates(sortOrder)
      .then((data) => {
        if (cancelled) return
        setInternalTemplates(excludeTemplateId ? data.filter((t) => t.id !== excludeTemplateId) : data)
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [externalTemplates, excludeTemplateId, sortOrder, sortInitialized])

  const templates = externalTemplates ?? internalTemplates
  const filteredTemplates = excludeTemplateId ? templates.filter((t) => t.id !== excludeTemplateId) : templates

  const handleSortChange = async (value: string) => {
    const next = value as TemplateSortOrder
    if (next === sortOrder) return
    // サーバーへの保存を先に確定させてから state を更新する。
    // これにより「一瞬 next 順で表示 → 失敗でロールバック」の二重 fetch / チラつきを回避し、
    // かつ外部モード時に親が listTemplates() を呼ぶ際 BodyProfile に確実に新順序が反映済みとなる。
    try {
      await updateBodyProfile({ template_sort_order: next })
    } catch (err) {
      console.warn('[TemplateFavoritesSection] updateBodyProfile failed:', err)
      toast.error('ソート順の保存に失敗しました')
      return
    }
    setSortOrder(next)
    // 外部モード: 親に再取得を促す（親側の listTemplates() は BodyProfile 経由で新順序を使う）
    if (externalTemplates) {
      onTemplateChanged?.()
    }
  }

  const handleDelete = async (id: number) => {
    if (!(await confirmDialog({ title: 'テンプレートを削除しますか？', destructive: true }))) return
    try {
      await deleteTemplate(id)
      if (!externalTemplates) {
        setInternalTemplates((prev) => prev.filter((t) => t.id !== id))
      }
      if (selectedIds.has(id)) {
        onToggle(id)
      }
      onTemplateChanged?.()
      toast.success('削除しました')
    } catch {
      toast.error('削除に失敗しました')
    }
  }

  if (loading) return <Loader2 className="mx-auto size-4 animate-spin text-muted-foreground" />

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between gap-2">
        <Select value={sortOrder} onValueChange={handleSortChange}>
          <SelectTrigger className="h-7 w-auto gap-1 border-transparent bg-transparent px-2 text-xs text-muted-foreground hover:bg-accent/40">
            <ArrowUpDown className="size-3" />
            <SelectValue />
          </SelectTrigger>
          <SelectContent align="start">
            {SORT_OPTIONS.map((opt) => (
              <SelectItem key={opt.value} value={opt.value} className="text-xs">
                {opt.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {onCreateTemplate && (
          <Button type="button" variant="secondary" size="sm" className="h-6 gap-1 text-xs" onClick={onCreateTemplate}>
            <Plus className="size-3" />
            登録
          </Button>
        )}
      </div>

      <TemplatePickerList
        templates={filteredTemplates}
        maxHeight={listMaxHeight}
        hideSearch={filteredTemplates.length <= 5}
        emptyMessage={onCreateTemplate ? '＋ボタンからお気に入りを登録できます' : 'テンプレートがありません'}
        caloriesLabel={(t) => `${Math.round(t.total_calories * (parseFloat(multipliers.get(t.id) ?? '') || 1))}kcal`}
        onItemClick={(t) => onToggle(t.id)}
        renderPrefix={(t) => <Checkbox checked={selectedIds.has(t.id)} onCheckedChange={() => onToggle(t.id)} />}
        renderActions={(t) => (
          <>
            {selectedIds.has(t.id) && (
              <div className="flex items-center gap-1">
                <span className="text-[10px] text-muted-foreground">×</span>
                <MultiplierInput
                  value={parseFloat(multipliers.get(t.id) ?? '') || 1}
                  onChange={(v) => onMultiplierChange(t.id, String(v))}
                />
              </div>
            )}
            {onEditTemplate && (
              <Button
                size="icon"
                variant="ghost"
                className="size-7 p-0! text-muted-foreground"
                onClick={() => onEditTemplate(t)}
              >
                <Pencil className="size-3.5" />
              </Button>
            )}
            <Button
              size="icon"
              variant="ghost"
              className="size-7 p-0! text-muted-foreground hover:text-destructive"
              onClick={() => handleDelete(t.id)}
            >
              <Trash2 className="size-3.5" />
            </Button>
          </>
        )}
      />
    </div>
  )
}
